<template>
  <div style="margin:20px">
    <FiPageRail title="Driver Actuals"/>

    <div class="actuals-cm-toolbar">
      <b-button-group size="sm" class="driver-actuals-timeframe">
        <b-button
          v-for="opt in timeframe_options"
          :key="opt.value"
          :variant="timeframeDays === opt.value ? 'primary' : 'outline-secondary'"
          v-on:click.prevent="set_timeframe(opt.value)"
        >{{ opt.label }}</b-button>
      </b-button-group>
      <b-button size="sm" variant="outline-secondary" class="toolbar-date-shift-btn" title="Previous day" v-on:click.prevent="shift_selected_date(-1)"><i class="fa fa-angle-left" aria-hidden="true"></i></b-button>
      <date-picker class="toolbar-date" v-model="selectedDate" :config="options" :placeholder="selectedDate"/>
      <b-button size="sm" variant="outline-secondary" class="toolbar-date-shift-btn" title="Next day" v-on:click.prevent="shift_selected_date(1)"><i class="fa fa-angle-right" aria-hidden="true"></i></b-button>
      <b-form-select size="sm" v-model="actuals_filter" :options="actuals_filter_options" class="toolbar-select"/>
      <b-form-select size="sm" v-if="company_options" v-model="selected_company" :options="company_options" class="toolbar-select toolbar-company"/>
      <b-form-select size="sm" v-if="type_options" v-model="selected_type" :options="type_options" class="toolbar-select toolbar-type"/>
      <b-button size="sm" v-on:click.prevent="load_data()">Search</b-button>
      <span class="toolbar-group">
        <span class="toolbar-label">Filter</span>
        <b-form-input size="sm" class="toolbar-input" v-model="booking_filter" placeholder=""/>
      </span>
      <b-form-select size="sm" v-if="vehicle_options" v-model="selected_vehicle" :options="vehicle_options" class="toolbar-select toolbar-vehicle"/>
      <b-form-select size="sm" v-if="show_base_select" v-model="selected_base" :options="base_options" class="toolbar-select toolbar-base"/>
    </div>

    <div v-if="!loading && !showResults" class="driver-actuals-base-prompt text-center text-muted my-4">
      <span v-if="awaitingBaseSelection">Select a base, then choose a date range and click <strong>Search</strong> to load results.</span>
      <span v-else-if="search_stale">Date or range changed. Click <strong>Search</strong> to load new results.</span>
      <span v-else>Select a date range and click <strong>Search</strong> to load results.</span>
    </div>

    <driver-actuals-filter-chips
      v-if="!loading && showResults"
      :items-full="items_full"
      :items-count="items.length"
      :selected-status="selected_status"
      @filter="filter_by_status"
    />

    <div v-if="loading" class="text-center my-4">
      <b-spinner class="align-middle"/>
    </div>

    <driver-actuals-timeline-table
      v-else-if="showResults"
      resource-type="driver"
      :fit-width="timeframeDays >= 3"
      :rows="timeline_display_rows"
      :timeline-days="timelineDays"
      :timeline-slots="timeline_slots"
      :timeline-slots-per-day="timelineSlotsPerDay"
      @cell-hover="onTimelineCellHover"
      @cell-leave="onTimelineCellLeave"
    />

    <driver-actuals-booking-panel
      :visible="booking_panel_visible"
      :details="booking_panel_details"
      :panel-style="booking_panel_style"
      variant="cm"
      @panel-enter="onBookingPanelEnter"
      @panel-leave="onBookingPanelLeave"
    />
  </div>
</template>

<script>
import driverActualsUtilHover from '@/mixins/driver_actuals_util_hover'
import DriverActualsFilterChips from '@/components/driver_actuals_filter_chips.vue'
import DriverActualsTimelineTable from '@/components/driver_actuals_timeline_table.vue'
import { prepareTimelineDisplayRows } from '@/helpers/actuals_timeline_cache'
import { mapState } from 'vuex'
import moment from 'moment'
import { justDate } from '@/helpers/dates'
import {
  awaitingBaseSelection,
  configureTenantBaseOptions,
} from '@/helpers/actuals_base_requirement'
import { getCmDriverStartEnd, filterDriverActualsByStatus } from '@/helpers/driver_actuals_times'
import { consecutiveEntriesOverlapMoreThanOneMinute } from '@/helpers/actuals_overlap'
import { get_actuals } from '../networking/apis'
import {
  DRIVER_ACTUALS_SLOTS_PER_DAY,
  DRIVER_ACTUALS_TIMELINE_SLOTS,
  DRIVER_ACTUALS_TIMEFRAME_OPTIONS,
  bookingOverlapsDateRange,
  buildDriverTimelineRows,
  enrichDriverDisplayRows,
  getCellBarClasses,
  getSegmentsAtCell,
  getTimelineDays,
  getTimelineEndDate,
} from '@/helpers/driver_actuals_timeline'

export default {
  name: 'driver_actuals',

  mixins: [driverActualsUtilHover],

  components: {
    DriverActualsFilterChips,
    DriverActualsTimelineTable,
  },

  data() {
    return {
      items: [],
      items_full: [],
      driver_rows: [],
      timeline_display_rows: [],
      summary: {},
      loading: false,
      company_options: null,
      vehicle_options: null,
      type_options: null,
      base_options: [],
      base_required: false,
      bases_configured: false,
      has_run: false,
      search_stale: false,
      selected_vehicle: '',
      selected_company: '',
      selected_base: '',
      selected_type: '',
      selectedDate: justDate(moment()),
      timeframeDays: 1,
      actuals_filter: 'finish',
      booking_filter: '',
      selected_status: null,
      actuals_filter_options: [
        { value: 'finish', text: 'By Finish Date' },
        { value: 'start', text: 'By Start Date' },
        { value: 'overlap', text: 'Overlaps Range' },
      ],
      timeline_slots: DRIVER_ACTUALS_TIMELINE_SLOTS,
      options: {
        format: 'MM-DD-YYYY',
        useCurrent: true,
        minDate: moment().subtract(720, 'days'),
        maxDate: moment().add(180, 'days'),
        keepOpen: false,
        showClear: true,
        showClose: true,
        toolbarPlacement: 'top',
      },
    }
  },

  created() {
    this.maybeAutoSearch()
  },

  methods: {
    maybeAutoSearch() {
      if (!this.user || !this.user.tenant)
        return
      if (this.loading || this.has_run)
        return
      this.load_data()
    },
    getDriverStartEnd(booking) {
      return getCmDriverStartEnd(booking)
    },
    filter_by_status(status) {
      this.selected_status = status
      this.filter_results()
    },
    bookingOverlapsTimeframe(booking) {
      const { start, end } = this.getDriverStartEnd(booking)
      return bookingOverlapsDateRange(start, end, this.selectedDate, this.timeframeDays)
    },
    set_timeframe(days) {
      if (this.timeframeDays === days)
        return
      this.timeframeDays = days
      this.invalidateSearchResults()
    },
    invalidateSearchResults() {
      if (this.has_run)
        this.search_stale = true
      this.has_run = false
    },
    updateTimelineTable(rows) {
      const timelineDays = getTimelineDays(this.selectedDate, this.timeframeDays)
      this.timeline_display_rows = prepareTimelineDisplayRows(rows, enrichDriverDisplayRows, timelineDays, {
        getCellBarClasses,
        getSegmentsAtCell,
      })
    },
    rebuildDriverRows() {
      const dayBookings = this.items.filter(booking => this.bookingOverlapsTimeframe(booking))
      const rows = buildDriverTimelineRows(
        dayBookings,
        (booking) => this.getDriverStartEnd(booking),
        'charterID'
      )
      this.driver_rows = rows
      this.updateTimelineTable(rows)
    },
    selectedDateApi() {
      return moment(this.selectedDate, 'MM-DD-YYYY').format('YYYY-MM-DD')
    },
    detect_driver_overlaps(items) {
      if (!items || !items.length)
        return
      for (const item of items) {
        item.driver_overlap = false
        item.driver_overlap_highlight_start = false
        item.driver_overlap_highlight_end = false
      }
      const groups = {}
      for (const item of items) {
        if (item.status === 'cancelled')
          continue
        const resource = item.driver
        if (!resource)
          continue
        const { start, end } = this.getDriverStartEnd(item)
        if (!start || !end)
          continue
        if (!groups[resource])
          groups[resource] = []
        groups[resource].push({ item, start, end, sortKey: item.start_datetime || start })
      }
      for (const resource of Object.keys(groups)) {
        const entries = groups[resource].sort((a, b) => new Date(a.sortKey) - new Date(b.sortKey))
        for (let i = 1; i < entries.length; i++) {
          const prev = entries[i - 1]
          const current = entries[i]
          if (!consecutiveEntriesOverlapMoreThanOneMinute(prev.end, current.start))
            continue
          prev.item.driver_overlap = true
          current.item.driver_overlap = true
        }
      }
    },
    resetTenantBases() {
      this.selected_base = ''
      this.base_required = false
      this.base_options = []
      this.bases_configured = false
    },
    applyBaseOptions(baseOptions) {
      const { base_required, base_options } = configureTenantBaseOptions(baseOptions)
      this.base_required = base_required
      this.base_options = base_options
      this.bases_configured = true
    },
    async load_data() {
      if (this.bases_configured && this.awaitingBaseSelection) {
        this.$emit('showAlert', 'please select a base before searching', 'warning', 5000)
        return
      }
      this.loading = true
      try {
        const fromDate = this.selectedDateApi()
        const toDate = getTimelineEndDate(this.selectedDate, this.timeframeDays)
        const { value: { data = [] } } = await get_actuals(
          this.user.tenant,
          this.actuals_filter,
          '',
          this.selected_company,
          this.selected_type,
          fromDate,
          toDate
        )
        this.summary = data.summary || {}
        if (this.summary.timezone)
          moment.tz.setDefault(this.summary.timezone)
        if (data.summary && data.summary.bases)
          this.applyBaseOptions(data.summary.bases)
        if (this.base_required && !this.selected_base) {
          this.items_full = []
          this.items = []
          this.timeline_display_rows = []
          this.has_run = false
          return
        }
        this.items_full = data.results || []
        this.detect_driver_overlaps(this.items_full)
        if (data.summary && data.summary.companies && data.summary.companies.length > 2)
          this.company_options = data.summary.companies
        this.vehicle_options = data.summary.vehicles
        this.type_options = data.summary.types
        this.filter_results()
        this.has_run = true
        this.search_stale = false
      } catch (e) {
        this.$emit('showAlert', 'error retrieving data, try logging in again or contact support if needed', 'danger', 5000)
      }
      this.loading = false
    },
    shift_selected_date(shift) {
      const next = moment(this.selectedDate, 'MM-DD-YYYY').add(shift, 'days')
      const min = moment(this.options.minDate)
      const max = moment(this.options.maxDate)
      if (next.isBefore(min, 'day'))
        this.selectedDate = min.format('MM-DD-YYYY')
      else if (next.isAfter(max, 'day'))
        this.selectedDate = max.format('MM-DD-YYYY')
      else
        this.selectedDate = next.format('MM-DD-YYYY')
    },
    contains(val1, val2) {
      if (val1 != null)
        return val1.toLowerCase().indexOf(val2.toLowerCase()) !== -1
      return false
    },
    filter_results() {
      let items = this.items_full
      if (!items) {
        this.items = []
        this.rebuildDriverRows()
        return
      }
      if (this.booking_filter) {
        const val = this.booking_filter
        if (this.booking_filter.indexOf('=') !== -1) {
          const field = this.booking_filter.split('=')[0]
          const value = this.booking_filter.split('=')[1]
          if (field === 'driver')
            items = items.filter(item => item.driver === value)
          else if (field === 'vehicle')
            items = items.filter(item => item.vehicle === value)
        } else {
          items = items.filter(item => (
            this.contains(item.bookingID, val)
            || this.contains(item.charterID, val)
            || this.contains(item.driver, val)
            || this.contains(item.vehicle, val)
          ))
        }
      }
      if (this.selected_company)
        items = items.filter(item => this.contains(item.companyID, this.selected_company))
      if (this.selected_vehicle)
        items = items.filter(item => this.contains(item.vehicle, this.selected_vehicle))
      if (this.selected_base) {
        items = items.filter(item => (
          this.contains(item.bookingBase, this.selected_base)
          || this.contains(item.startBase, this.selected_base)
          || this.contains(item.finishBase, this.selected_base)
        ))
      }
      this.items = filterDriverActualsByStatus(items, this.selected_status)
      this.rebuildDriverRows()
    },
  },

  computed: {
    ...mapState({
      user: state => state.profile.user,
    }),
    show_base_select() {
      return this.base_required || (this.base_options && this.base_options.length > 2)
    },
    awaitingBaseSelection() {
      return awaitingBaseSelection(this.base_required, this.selected_base)
    },
    showResults() {
      return this.has_run && !this.awaitingBaseSelection
    },
    timeframe_options() {
      return DRIVER_ACTUALS_TIMEFRAME_OPTIONS
    },
    timelineDays() {
      return getTimelineDays(this.selectedDate, this.timeframeDays)
    },
    timelineSlotsPerDay() {
      return DRIVER_ACTUALS_SLOTS_PER_DAY
    },
  },

  watch: {
    user: {
      deep: true,
      handler() {
        this.has_run = false
        this.search_stale = false
        this.resetTenantBases()
        this.maybeAutoSearch()
      },
    },
    selectedDate() {
      this.invalidateSearchResults()
    },
    booking_filter() {
      if (this.has_run)
        this.scheduleFilterResults()
    },
    selected_base() {
      if (this.has_run && !this.base_required)
        this.filter_results()
    },
    selected_vehicle() {
      if (this.has_run)
        this.filter_results()
    },
    selected_company() {
      if (this.has_run)
        this.filter_results()
    },
  },
}
</script>

<style>
.driver-actuals-timeframe {
  flex-shrink: 0;
}
</style>

<style>
.actuals-cm-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 6px 8px;
  font-size: 14px;
  font-weight: bold;
  margin: 14px 8px 10px;
  position: relative;
}
.actuals-cm-toolbar .toolbar-date-shifts { flex-shrink: 0; }
.actuals-cm-toolbar .toolbar-date-shift-btn {
  padding: 0.15rem 0.35rem;
  line-height: 1;
  min-width: 1.65rem;
}
.actuals-cm-toolbar .toolbar-date {
  font-size: 14px;
  font-weight: bold;
  width: 108px;
  padding: 0.2rem 0.45rem;
  height: calc(1.5em + 0.5rem + 2px);
}
.actuals-cm-toolbar .toolbar-select {
  font-size: 14px;
  font-weight: bold;
  width: 150px;
}
.actuals-cm-toolbar .toolbar-company { width: 160px; }
.actuals-cm-toolbar .toolbar-type { width: 130px; }
.actuals-cm-toolbar .toolbar-vehicle { width: 120px; }
.actuals-cm-toolbar .toolbar-base { width: 130px; }
.actuals-cm-toolbar .toolbar-input { width: 108px; }
.actuals-cm-toolbar .toolbar-label {
  white-space: nowrap;
  font-size: 12px;
  font-weight: 600;
  color: #6c757d;
}
.actuals-cm-toolbar .toolbar-group {
  display: inline-flex;
  align-items: center;
  gap: 4px 6px;
  flex-shrink: 0;
}
.driver-actuals-base-prompt {
  font-size: 18px;
  color: #6c757d;
  margin: 40px 30px;
}
</style>
